/**
 * code translated from mupdf (c)
 */

export interface FzMatrix {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

export interface FzRect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export function initFzMatrix(a: number, b: number, c: number, d: number, e: number, f: number): FzMatrix {
    return { a, b, c, d, e, f };
  }

export function scaleFzRect(r: FzRect, sx: number, sy: number): FzRect {
    const width = r.x1 - r.x0;
    const height = r.y1 - r.y0;
    const scaledWidth = width * sx;
    const scaledHeight = height * sy;
    const centerX = (r.x0 + r.x1) / 2;
    const centerY = (r.y0 + r.y1) / 2;
    const scaledX0 = centerX - (scaledWidth / 2);
    const scaledY0 = centerY - (scaledHeight / 2);
    const scaledX1 = centerX + (scaledWidth / 2);
    const scaledY1 = centerY + (scaledHeight / 2);
    return { x0: scaledX0, y0: scaledY0, x1: scaledX1, y1: scaledY1 };
  }
  
interface FzPoint {
  x: number;
  y: number;
}

const FZ_MIN_INF_RECT: number = -0x80000000; // assuming 32-bit integer
const FZ_MAX_INF_RECT: number = 0x7fffff80; // assuming 32-bit integer
function fz_max(a: number, b: number): number {
  return a > b ? a : b;
}

function fz_min(a: number, b: number): number {
  return a < b ? a : b;
}

function MAX4(a: number, b: number, c: number, d: number): number {
  return fz_max(fz_max(a, b), fz_max(c, d));
}

function MIN4(a: number, b: number, c: number, d: number): number {
  return fz_min(fz_min(a, b), fz_min(c, d));
}
function fz_is_infinite_rect(r: FzRect): boolean {
  return (
    r.x0 === FZ_MIN_INF_RECT &&
    r.x1 === FZ_MAX_INF_RECT &&
    r.y0 === FZ_MIN_INF_RECT &&
    r.y1 === FZ_MAX_INF_RECT
  );
}

function fz_transform_point_xy(x: number, y: number, m: FzMatrix): FzPoint {
  const p: FzPoint = {
    x: x * m.a + y * m.c + m.e,
    y: x * m.b + y * m.d + m.f,
  };
  return p;
}

function fz_transform_point(p: FzPoint, m: FzMatrix): FzPoint {
  const x = p.x;
  p.x = x * m.a + p.y * m.c + m.e;
  p.y = x * m.b + p.y * m.d + m.f;
  return p;
}

export function fz_transform_rect(r: FzRect, m: FzMatrix): FzRect {
  let s, t, u, v: FzPoint;
  let invalid: boolean;

  if (fz_is_infinite_rect(r)) {
    return r;
  }

  if (Math.abs(m.b) < Number.EPSILON && Math.abs(m.c) < Number.EPSILON) {
    if (m.a < 0) {
      const f = r.x0;
      r.x0 = r.x1;
      r.x1 = f;
    }
    if (m.d < 0) {
      const f = r.y0;
      r.y0 = r.y1;
      r.y1 = f;
    }
    s = fz_transform_point_xy(r.x0, r.y0, m);
    t = fz_transform_point_xy(r.x1, r.y1, m);
    r.x0 = s.x;
    r.y0 = s.y;
    r.x1 = t.x;
    r.y1 = t.y;
    // If r was invalid coming in, it'll still be invalid now.
    return r;
  } else if (Math.abs(m.a) < Number.EPSILON && Math.abs(m.d) < Number.EPSILON) {
    if (m.b < 0) {
      const f = r.x0;
      r.x0 = r.x1;
      r.x1 = f;
    }
    if (m.c < 0) {
      const f = r.y0;
      r.y0 = r.y1;
      r.y1 = f;
    }
    s = fz_transform_point_xy(r.x0, r.y0, m);
    t = fz_transform_point_xy(r.x1, r.y1, m);
    r.x0 = s.x;
    r.y0 = s.y;
    r.x1 = t.x;
    r.y1 = t.y;
    // If r was invalid coming in, it'll still be invalid now.
    return r;
  }

  invalid = r.x0 > r.x1 || r.y0 > r.y1;
  s = { x: r.x0, y: r.y0 };
  t = { x: r.x0, y: r.y1 };
  u = { x: r.x1, y: r.y1 };
  v = { x: r.x1, y: r.y0 };
  s = fz_transform_point(s, m);
  t = fz_transform_point(t, m);
  u = fz_transform_point(u, m);
  v = fz_transform_point(v, m);
  r.x0 = MIN4(s.x, t.x, u.x, v.x);
  r.y0 = MIN4(s.y, t.y, u.y, v.y);
  r.x1 = MAX4(s.x, t.x, u.x, v.x);
  r.y1 = MAX4(s.y, t.y, u.y, v.y);

  // If we were called with an invalid rectangle, return an
  // invalid rectangle after transformation.
  if (invalid) {
    let t: number;
    t = r.x0;
    r.x0 = r.x1;
    r.x1 = t;
    t = r.y0;
    r.y0 = r.y1;
    r.y1 = t;
  }
  return r;
}
